import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, Loader2, Minimize2 } from 'lucide-react';
import api from '../lib/api';

const SUGGESTIONS = [
  "Comment passer une commande ?",
  "Quels produits sont disponibles ?",
  "Comment configurer ma livraison ?",
  "كيف أطلب منتجاً ؟",
];

const CliniqAgent = () => {
  const [open,     setOpen]     = useState(false);
  const [messages, setMessages] = useState([]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const [minimized,setMinimized]= useState(false);
  const bottomRef = useRef(null);
  const inputRef  = useRef(null);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        content: "Bonjour ! 👋 Je suis l'assistant Comptoir-Online. Comment puis-je vous aider aujourd'hui ?\n\nمرحباً ! أنا مساعد Comptoir-Online. كيف يمكنني مساعدتك ؟",
      }]);
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (open && !minimized) inputRef.current?.focus();
  }, [open, minimized]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput('');

    const userMsg = { role: 'user', content: msg };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setLoading(true);

    try {
      const res = await api.post('/chat', {
        messages: newMessages.map(m => ({ role: m.role, content: m.content })),
      });
      const reply = res.data?.reply || "Je suis désolé, je n'ai pas pu traiter votre demande.";
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Une erreur s'est produite. Veuillez réessayer plus tard ou contacter le support.",
      }]);
    } finally {
      setLoading(false);
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#1F4D3F] hover:bg-[#173B30] text-white shadow-2xl flex items-center justify-center transition-all hover:scale-110 group"
        aria-label="Assistant Comptoir-Online"
      >
        <MessageCircle className="w-6 h-6" />
        <span className="absolute -top-10 right-0 bg-[#1F4D3F] text-white text-xs px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
          Assistant Comptoir-Online
        </span>
        {/* Badge notification */}
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#C75B27] rounded-full flex items-center justify-center text-[9px] font-bold">
          1
        </span>
      </button>
    );
  }

  return (
    <div className={`fixed bottom-6 right-6 z-50 w-[360px] bg-white rounded-2xl shadow-2xl border border-[#E8E0D0] flex flex-col overflow-hidden transition-all ${minimized ? 'h-14' : 'h-[520px]'}`}>

      {/* Header */}
      <div className="bg-[#1F4D3F] px-4 py-3 flex items-center gap-3 shrink-0">
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
          <Bot className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">Assistant Comptoir-Online</div>
          <div className="text-[10px] text-white/60 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block"></span>
            En ligne — Répond en FR et AR
          </div>
        </div>
        <button onClick={() => setMinimized(m => !m)} className="text-white/60 hover:text-white p-1">
          <Minimize2 className="w-4 h-4" />
        </button>
        <button onClick={() => setOpen(false)} className="text-white/60 hover:text-white p-1">
          <X className="w-4 h-4" />
        </button>
      </div>

      {!minimized && (
        <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FAFAF8]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                {m.role === 'assistant' && (
                  <div className="w-7 h-7 rounded-full bg-[#E4EDE9] flex items-center justify-center mr-2 mt-1 shrink-0">
                    <Bot className="w-3.5 h-3.5 text-[#1F4D3F]" />
                  </div>
                )}
                <div className={`max-w-[78%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap
                  ${m.role === 'user'
                    ? 'bg-[#1F4D3F] text-white rounded-br-sm'
                    : 'bg-white text-[#2D2A26] rounded-bl-sm border border-[#E8E0D0] shadow-sm'}`}>
                  {m.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="w-7 h-7 rounded-full bg-[#E4EDE9] flex items-center justify-center mr-2 shrink-0">
                  <Bot className="w-3.5 h-3.5 text-[#1F4D3F]" />
                </div>
                <div className="bg-white border border-[#E8E0D0] rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
                  <Loader2 className="w-4 h-4 text-[#1F4D3F] animate-spin" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Suggestions (premier message uniquement) */}
          {messages.length === 1 && (
            <div className="px-4 pb-3 flex flex-wrap gap-2 bg-[#FAFAF8]">
              {SUGGESTIONS.map((s, i) => (
                <button key={i} onClick={() => sendMessage(s)}
                  className="text-[11px] px-3 py-1.5 rounded-full bg-white border border-[#E8E0D0] text-[#5A5247] hover:border-[#1F4D3F] hover:text-[#1F4D3F] transition-colors">
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="px-3 py-3 border-t border-[#E8E0D0] bg-white flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Posez votre question... / اكتب سؤالك..."
              rows={1}
              className="flex-1 resize-none border border-[#E8E0D0] rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4D3F] bg-[#FAFAF8] max-h-24 leading-5"
              style={{ overflowY: 'auto' }}
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className="w-9 h-9 rounded-xl bg-[#1F4D3F] hover:bg-[#173B30] text-white flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed transition-colors shrink-0"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <div className="text-center pb-2 text-[9px] text-[#C9BFAE]">Propulsé par Claude AI · Comptoir-Online</div>
        </>
      )}
    </div>
  );
};

export default CliniqAgent;
